import axios from 'axios'

// Sanctum SPA session auth against the API's own origin (cross-subdomain:
// app.typhoonhq.* -> api.typhoonhq.*). withCredentials sends the session
// cookie; withXSRFToken makes axios read the XSRF-TOKEN cookie and send it as
// the X-XSRF-TOKEN header on every mutating request. CORS + shared cookie
// domain on the API side make this work without a same-origin proxy.
const apiOrigin = import.meta.env.VITE_APP_API_BASE_URL

const api = axios.create({
  baseURL: `${apiOrigin}/api`,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

// The CSRF cookie must be primed before the first login/mutation.
export function fetchCsrfCookie(): Promise<unknown> {
  return axios.get(`${apiOrigin}/sanctum/csrf-cookie`, { withCredentials: true })
}

// Called by the store on a 401 to redirect to /login. Registered from the app
// entrypoint to keep the router out of this module (avoids an import cycle).
let onUnauthorized: (() => void) | null = null

export function registerUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler
}

// Proactive session-expiry detection. Laravel's session lifetime is a sliding
// window (SESSION_LIFETIME, refreshed on every request), so we mirror it
// client-side as a wall-clock deadline rather than trusting a single timer to
// fire: a setTimeout scheduled for 120 minutes out does not reliably survive
// the OS suspending the tab/laptop overnight (background tabs get throttled
// or fully paused), so on wake the timer may never go off. The deadline
// itself is authoritative and cheap to re-check — from the timer (best effort
// while the tab stays active), and from the router guard on every navigation
// (reliable, since the user's next click always runs it). Must match the
// API's SESSION_LIFETIME (minutes).
const SESSION_LIFETIME_MINUTES = 120

let sessionExpiresAt: number | null = null
let sessionExpiryTimer: ReturnType<typeof setTimeout> | undefined

function scheduleSessionExpiry(): void {
  sessionExpiresAt = Date.now() + SESSION_LIFETIME_MINUTES * 60 * 1000
  clearTimeout(sessionExpiryTimer)
  sessionExpiryTimer = setTimeout(() => onUnauthorized?.(), SESSION_LIFETIME_MINUTES * 60 * 1000)
}

// Ground-truth check independent of the timer above. Called by the router
// guard on every navigation so an expiry the timer missed (system asleep) is
// still caught on the user's next interaction.
export function hasSessionExpired(): boolean {
  return sessionExpiresAt !== null && Date.now() >= sessionExpiresAt
}

api.interceptors.response.use(
  (response) => {
    scheduleSessionExpiry()
    return response
  },
  (error) => {
    const status = error?.response?.status
    // Only a response that actually reached the server means the session was
    // touched server-side. Network failures, timeouts, and aborted/CORS
    // requests never got there, so they shouldn't slide the deadline forward.
    if (error?.response) {
      scheduleSessionExpiry()
    }
    const url: string = error?.config?.url ?? ''
    const isAuthEndpoint = url.includes('/auth/')
    // 419 is Sanctum's CSRF/session-token-mismatch response — it means the
    // session backing the CSRF token is already gone, same as a 401.
    if ((status === 401 || status === 419) && !isAuthEndpoint) {
      onUnauthorized?.()
    }
    return Promise.reject(error)
  },
)

// Laravel API Resources wrap every response in { data: T }. Callers pass the
// in-flight request and get the unwrapped payload back.
export async function unwrap<T>(request: Promise<{ data: { data: T } }>): Promise<T> {
  const { data } = await request
  return data.data
}

export default api
